// src/utils/executiveMetrics.js
//
// THE EXECUTIVE METRIC LAYER — pure JavaScript, no React, no Firebase.
//
// Every headline number in the enterprise console is derived here from three
// inputs and nothing else:
//
//   1. ERI — the organization's composite readiness from the warehouse
//      (v3: performance + wellbeing + cognitive), with the quadrant shares
//      behind it (Running Hot / Needs Support). Arrives in the live snapshot
//      from getEnterpriseSnapshot; the edition's modeled example stands in
//      until an org has data above the privacy floor.
//   2. The organization's own business data — typed in by an admin and saved
//      on organizations/{id}.businessInputs, or uploaded through the data
//      integration layer (enterprise_workforce_metrics -> live.business).
//   3. Business averages — conservative, published-style benchmarks
//      (EXEC_BENCHMARKS, with per-edition overrides) that stand in wherever
//      the client has no figure AND act as the comparison line.
//
// Precedence for a business input: client figure > uploaded figure >
// business average. Every value carries its `source`, and the UI labels it:
// client data / uploaded / benchmark / modeled / observed.
//
// Model documentation: docs/EXECUTIVE-METRICS.md.

export const EXEC_BENCHMARKS = {
    // Business averages — used where the client has no figure, and as the
    // comparison line ("against the business average").
    absenceDays: 8.5,           // absence days / employee / yr (StatCan-style)
    turnoverRate: 16,           // % voluntary separations / yr
    efapUtilization: 8,         // % baseline EAP / EFAP utilization
    // Cost & effect assumptions (deliberately conservative).
    replacementMultiple: 0.75,  // turnover replacement cost × salary
    stdClaimCost: 20000,        // modeled average stress-related STD claim cost
    stdIncidence: 2.5,          // % of workforce filing an STD claim / yr
    valuePerEngagement: 1200,   // modeled value of one earlier support engagement
    absenceReduction: 8,        // % reduction in lost days attributable to the program
    turnoverReduction: 5,       // % reduction in voluntary separations
    efapLift: 20,               // relative % lift in utilization (routing to existing benefits)
    stdReduction: 6,            // % reduction in STD claims
    ltdTransitionRate: 0.30,    // share of STD claims that transition to LTD (the tail)
    // ERI -> risk. An org sitting AT the reference ERI carries the average
    // incidence; every point below adds 1% expected claims, every point above
    // removes 1%, clamped to [riskFloor, riskCeiling]. Modeled, not measured.
    eriReference: 60,
    riskFloor: 0.75,
    riskCeiling: 1.5,
    outlookBand: 0.2            // ± band around the modeled midpoint (never a point forecast)
};

export const WORKING_DAYS = 260;
export const ATTRIBUTION_MIN = 5;
export const ATTRIBUTION_MAX = 40;   // hard ceiling well below 100% — never full causality
export const DEFAULT_ATTRIBUTION = 20;
export const DEMO_LABOUR_COST = 85000;

// Demo-only readiness signals, used when no live snapshot exists.
export const ADOPTION_SCENARIOS = {
    Conservative: { capacity: 71, activation: 58, engagement: 49 },
    Expected:     { capacity: 82, activation: 71, engagement: 64 },
    Strong:       { capacity: 88, activation: 83, engagement: 77 }
};

// The business inputs the model consumes. `upload` is the canonical warehouse
// metric name that can auto-populate the field; `bench` the average that
// stands in; `direction` says which way is better for the comparison strip.
export const INPUT_FIELDS = [
    { key: 'eligibleEmployees', label: 'Eligible employees', required: true, upload: 'headcount', unit: '', step: 1 },
    { key: 'loadedLabourCost', label: 'Loaded labour cost', required: true, upload: 'loaded_labour_cost', prefix: '$', unit: '/ yr', step: 1000 },
    { key: 'absenceDays', label: 'Baseline absence days', upload: 'absence_days_per_fte', unit: '/ emp / yr', step: 0.5, bench: 'absenceDays', direction: 'lower' },
    { key: 'turnoverRate', label: 'Voluntary turnover', upload: 'turnover_voluntary_rate', unit: '% / yr', step: 0.5, bench: 'turnoverRate', direction: 'lower' },
    { key: 'efapUtilization', label: 'Baseline EAP utilization', upload: 'efap_utilization_rate', unit: '%', step: 0.5, bench: 'efapUtilization', direction: 'higher' }
];

// Editable assumptions (the "Industry benchmarks & modeled assumptions" panel).
export const BENCH_FIELDS = [
    { key: 'absenceDays', label: 'Avg absence days', unit: '/ emp / yr', step: 0.5 },
    { key: 'turnoverRate', label: 'Avg voluntary turnover', unit: '% / yr', step: 0.5 },
    { key: 'efapUtilization', label: 'Avg EAP utilization', unit: '%', step: 0.5 },
    { key: 'eriReference', label: 'ERI reference (average)', unit: '/100', step: 1 },
    { key: 'replacementMultiple', label: 'Replacement cost', unit: '× salary', step: 0.1 },
    { key: 'stdClaimCost', label: 'Avg STD claim cost', prefix: '$', step: 500 },
    { key: 'stdIncidence', label: 'STD incidence', unit: '% / yr', step: 0.1 },
    { key: 'valuePerEngagement', label: 'Value / early engagement', prefix: '$', step: 100 },
    { key: 'absenceReduction', label: 'Absence reduction', unit: '%', step: 0.5 },
    { key: 'turnoverReduction', label: 'Turnover reduction', unit: '%', step: 0.5 },
    { key: 'efapLift', label: 'EAP utilization lift', unit: '% rel.', step: 1 },
    { key: 'stdReduction', label: 'STD claim reduction', unit: '%', step: 0.5 }
];

/* ------------------------------------------------------------------ */
/*  helpers                                                            */
/* ------------------------------------------------------------------ */

const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : null;
};
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const round = (v, d = 0) => { const f = Math.pow(10, d); return Math.round(v * f) / f; };

// Currency formatting follows the edition (CAD/en-CA for insurance,
// USD/en-US for aviation).
export const makeFormatters = (locale = 'en-US', currency = 'USD') => {
    const money = (n) => {
        const v = Number.isFinite(n) ? n : 0;
        return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);
    };
    const compact = (n) => {
        const v = Number.isFinite(n) ? n : 0;
        const abs = Math.abs(v);
        if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
        if (abs >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
        return money(v);
    };
    return { money, compact };
};

// First-to-last movement of a trend, ignoring suppressed (null) points.
export const trendDelta = (points) => {
    const vals = (points || []).map(p => (p ? p.capacity : null)).filter(v => typeof v === 'number');
    return vals.length > 1 ? Math.round(vals[vals.length - 1] - vals[0]) : 0;
};

/* ------------------------------------------------------------------ */
/*  1. benchmarks (business averages)                                  */
/* ------------------------------------------------------------------ */

export const benchmarksFor = (edition, overrides = {}) => {
    const roi = (edition && edition.roi) || {};
    const out = { ...EXEC_BENCHMARKS };
    if (num(roi.replacementMultiple) !== null) out.replacementMultiple = num(roi.replacementMultiple);
    if (num(roi.turnoverRate) !== null) out.turnoverRate = num(roi.turnoverRate);
    Object.keys(overrides || {}).forEach((k) => {
        const v = num(overrides[k]);
        if (v !== null) out[k] = v;
    });
    return out;
};

/* ------------------------------------------------------------------ */
/*  2. business inputs (client > upload > benchmark)                   */
/* ------------------------------------------------------------------ */

export const resolveBusinessInputs = ({ saved = {}, uploaded = {}, bench = EXEC_BENCHMARKS, edition = null, isLive = false }) => {
    const s = saved || {};
    const up = uploaded || {};
    const useBench = s.useBenchmark || {};
    const summary = (edition && edition.summary) || {};
    const out = {};

    INPUT_FIELDS.forEach((f) => {
        const clientVal = num(s[f.key]);
        const uploadRow = f.upload ? up[f.upload] : null;
        const uploadVal = uploadRow ? num(uploadRow.value) : null;
        const benchVal = f.bench ? num(bench[f.bench]) : null;

        if (f.bench && useBench[f.key]) {
            out[f.key] = { value: benchVal, source: 'benchmark' };
        } else if (clientVal !== null) {
            out[f.key] = { value: clientVal, source: 'client', updatedAt: s.updatedAt || null };
        } else if (uploadVal !== null) {
            out[f.key] = { value: uploadVal, source: 'upload', asOf: uploadRow.asOf || null, via: uploadRow.source || null };
        } else if (benchVal !== null) {
            out[f.key] = { value: benchVal, source: 'benchmark' };
        } else if (!isLive) {
            // Demo: the edition's modeled workforce keeps the sales example whole.
            const demo = f.key === 'eligibleEmployees' ? (num(summary.workforce) ?? 1200) : DEMO_LABOUR_COST;
            out[f.key] = { value: demo, source: 'modeled' };
        } else {
            out[f.key] = { value: null, source: 'missing' };
        }
    });

    const pepm = num(s.programPEPM);
    out.programPEPM = { value: pepm, source: pepm !== null ? 'client' : 'none' };
    out.attribution = clamp(num(s.attribution) ?? DEFAULT_ATTRIBUTION, ATTRIBUTION_MIN, ATTRIBUTION_MAX);
    return out;
};

/* ------------------------------------------------------------------ */
/*  3. readiness engine (ERI -> attribution index)                     */
/* ------------------------------------------------------------------ */

// Uplift-only: stronger aggregate readiness earns the program toward its
// attribution ceiling. Floored at 1.0 — engagement is rewarded, never assumed.
export const readinessIndexFrom = (blended) => round(Math.max(1, 1 + 0.40 * (blended - 0.55)), 2);

export const readinessFrom = ({ live, scenario = 'Expected' }) => {
    const isLive = live && live.source === 'live';
    const capacity = isLive ? (num(live.eri && live.eri.score) ?? num(live.capacityScore)) : null;
    if (isLive && capacity !== null) {
        const activation = num(live.participation && live.participation.rate);
        const parts = [capacity, activation].filter(v => v !== null);
        const blended = parts.reduce((s, v) => s + v, 0) / (parts.length * 100);
        return {
            source: 'live',
            basis: live.capacityBasis || 'performance',
            capacity,
            activation,
            engagement: null,
            perf: num(live.eri && live.eri.perf),
            wellbeing: num(live.eri && live.eri.wellbeing),
            blended: round(blended, 3),
            readinessIndex: readinessIndexFrom(blended)
        };
    }
    const sc = ADOPTION_SCENARIOS[scenario] || ADOPTION_SCENARIOS.Expected;
    const blended = (sc.capacity + sc.activation + sc.engagement) / 300;
    return { source: 'modeled', basis: 'scenario', scenario, ...sc, perf: null, wellbeing: null, blended: round(blended, 3), readinessIndex: readinessIndexFrom(blended) };
};

/* ------------------------------------------------------------------ */
/*  4. the Running Hot cohort                                          */
/* ------------------------------------------------------------------ */

export const cohortFrom = ({ live, edition }) => {
    const isLive = live && live.source === 'live';
    if (isLive) {
        const responded = num(live.participation && live.participation.responded) ?? 0;
        const q = live.quadrants || {};
        const rh = num(q.running_hot);
        const ns = num(q.needs_support);
        if (rh === null && ns === null) {
            // Live, but the derived signals haven't reached the warehouse yet.
            return { source: 'pending', responded, runningHotShare: null, needsSupportShare: null, strainShare: null, runningHotCount: null, strainCount: null };
        }
        const strain = (rh || 0) + (ns || 0);
        return {
            source: 'observed',
            responded,
            runningHotShare: rh,
            needsSupportShare: ns,
            strainShare: round(strain, 4),
            thrivingShare: num(q.thriving),
            buildingShare: num(q.building),
            unknownShare: num(q.unknown),
            divergenceRate: num(live.divergenceRate),
            runningHotCount: Math.round((rh || 0) * responded),
            strainCount: Math.round(strain * responded)
        };
    }
    const s = (edition && edition.summary) || {};
    const responded = Math.round((num(s.workforce) || 0) * ((num(s.participationRate) || 0) / 100));
    const cohort = num(s.highSignalCohort);
    const share = responded > 0 && cohort !== null ? round(cohort / responded, 4) : null;
    return { source: 'modeled', responded, runningHotShare: share, needsSupportShare: null, strainShare: share, runningHotCount: cohort, strainCount: cohort };
};

/* ------------------------------------------------------------------ */
/*  5. outlook (ERI-adjusted STD / leave range)                        */
/* ------------------------------------------------------------------ */

export const riskMultiplierFor = (eri, bench = EXEC_BENCHMARKS) => {
    const e = num(eri);
    if (e === null) return 1;
    return clamp(1 + (bench.eriReference - e) / 100, bench.riskFloor, bench.riskCeiling);
};

export const outlookFrom = ({ live, edition, eligible, bench = EXEC_BENCHMARKS, windowDays = 90 }) => {
    const isLive = live && live.source === 'live';
    const eri = isLive ? (num(live.eri && live.eri.score) ?? num(live.capacityScore)) : null;
    const N = num(eligible) || 0;

    if (isLive && eri !== null && N > 0) {
        const mult = riskMultiplierFor(eri, bench);
        const annual = N * (bench.stdIncidence / 100);
        const mid = annual * (windowDays / 365) * mult;
        const low = Math.max(0, Math.round(mid * (1 - bench.outlookBand)));
        const high = Math.max(low, Math.round(mid * (1 + bench.outlookBand)));
        return {
            basis: 'live', source: 'modeled', windowDays, eri,
            riskMultiplier: round(mult, 2), annualExpected: round(annual, 1), mid: round(mid, 1),
            low, high,
            ltdLow: Math.round(low * bench.ltdTransitionRate),
            ltdHigh: Math.round(high * bench.ltdTransitionRate)
        };
    }
    const s = (edition && edition.summary) || {};
    const m = s.modeledStdClaims || { low: 0, high: 0, windowDays };
    const l = s.modeledLtdTransition || {};
    return {
        basis: 'demo', source: 'modeled', windowDays: m.windowDays || windowDays, eri: null,
        riskMultiplier: 1, annualExpected: null, mid: null,
        low: m.low, high: m.high, ltdLow: l.low ?? null, ltdHigh: l.high ?? null
    };
};

/* ------------------------------------------------------------------ */
/*  6. the five-term financial model                                   */
/* ------------------------------------------------------------------ */

export const financialFrom = ({ inputs, bench = EXEC_BENCHMARKS, attribution = DEFAULT_ATTRIBUTION, readinessIndex = 1, riskMultiplier = 1 }) => {
    const N = num(inputs.eligibleEmployees && inputs.eligibleEmployees.value) || 0;
    const S = num(inputs.loadedLabourCost && inputs.loadedLabourCost.value) || 0;
    const complete = N > 0 && S > 0;
    const A = (clamp(attribution, ATTRIBUTION_MIN, ATTRIBUTION_MAX) / 100) * readinessIndex; // engagement-adjusted attribution
    const dailyCost = S / WORKING_DAYS;

    // 1. Avoided Lost Time
    const days = num(inputs.absenceDays && inputs.absenceDays.value) || 0;
    const avoidedDays = N * days * (bench.absenceReduction / 100);
    const avoidedLostTime = avoidedDays * dailyCost * A;

    // 2. Avoided Turnover
    const turnover = (num(inputs.turnoverRate && inputs.turnoverRate.value) || 0) / 100;
    const replacementCost = S * bench.replacementMultiple;
    const avoidedSeparations = N * turnover * (bench.turnoverReduction / 100);
    const avoidedTurnover = avoidedSeparations * replacementCost * A;

    // 3. Benefits Utilization Lift
    const util = (num(inputs.efapUtilization && inputs.efapUtilization.value) || 0) / 100;
    const extraEngagements = N * util * (bench.efapLift / 100);
    const benefitsLift = extraEngagements * bench.valuePerEngagement * A;

    // 4. Risk Reduction (STD / LTD) — lagging, lowest confidence. The org's ERI
    //    scales the expected claims (riskMultiplier) before the modeled reduction.
    const expectedClaims = N * (bench.stdIncidence / 100) * riskMultiplier;
    const avoidedClaims = expectedClaims * (bench.stdReduction / 100);
    const riskReduction = avoidedClaims * bench.stdClaimCost * A;

    // 5. Program Cost (indicative only)
    const programCost = N * (num(inputs.programPEPM && inputs.programPEPM.value) || 0) * 12;

    const grossValue = complete ? avoidedLostTime + avoidedTurnover + benefitsLift + riskReduction : 0;
    const netRoi = grossValue - programCost;
    const ratio = programCost > 0 ? grossValue / programCost : 0;

    return {
        complete,
        avoidedLostTime: complete ? avoidedLostTime : 0,
        avoidedTurnover: complete ? avoidedTurnover : 0,
        benefitsLift: complete ? benefitsLift : 0,
        riskReduction: complete ? riskReduction : 0,
        programCost, grossValue, netRoi, ratio,
        effectiveAttribution: A,
        avoidedDays, avoidedSeparations, expectedClaims, avoidedClaims
    };
};

/* ------------------------------------------------------------------ */
/*  7. against the business average                                    */
/* ------------------------------------------------------------------ */

export const compareFrom = ({ inputs, bench = EXEC_BENCHMARKS, readiness }) => {
    const rows = [];
    if (readiness && readiness.source === 'live' && typeof readiness.capacity === 'number') {
        const avg = bench.eriReference;
        const deltaPct = avg ? round(((readiness.capacity - avg) / avg) * 100, 0) : null;
        rows.push({ key: 'eri', label: 'ERI · composite readiness', unit: '/100', value: readiness.capacity, average: avg, deltaPct, better: deltaPct === 0 || deltaPct === null ? null : deltaPct > 0, source: 'observed', direction: 'higher' });
    }
    INPUT_FIELDS.filter(f => f.bench).forEach((f) => {
        const row = inputs[f.key];
        const v = row ? num(row.value) : null;
        const avg = num(bench[f.bench]);
        if (v === null || avg === null) return;
        const deltaPct = avg ? round(((v - avg) / avg) * 100, 0) : null;
        const better = deltaPct === null || deltaPct === 0 ? null : (f.direction === 'lower' ? deltaPct < 0 : deltaPct > 0);
        rows.push({ key: f.key, label: f.label, unit: f.unit, value: v, average: avg, deltaPct, better, source: row.source, direction: f.direction });
    });
    return rows;
};

/* ------------------------------------------------------------------ */
/*  8. everything, once                                                */
/* ------------------------------------------------------------------ */

export const computeExecutiveMetrics = ({ live, edition, saved = {}, uploaded, benchOverrides = {}, attribution, scenario = 'Expected', windowDays } = {}) => {
    const ed = edition || {};
    const isLive = !!(live && live.source === 'live');
    const bench = benchmarksFor(ed, benchOverrides);
    // Uploaded business metrics ride in the live snapshot; a caller may pass
    // its own map, but never has to remember to.
    const up = uploaded || (live && live.business) || {};
    const inputs = resolveBusinessInputs({ saved, uploaded: up, bench, edition: ed, isLive });
    const attr = clamp(num(attribution) ?? inputs.attribution, ATTRIBUTION_MIN, ATTRIBUTION_MAX);
    const readiness = readinessFrom({ live, scenario });
    const cohort = cohortFrom({ live, edition: ed });
    const eligible = num(inputs.eligibleEmployees && inputs.eligibleEmployees.value) || 0;
    const window = num(windowDays) || num(live && live.windowDays) || 90;
    const outlook = outlookFrom({ live, edition: ed, eligible, bench, windowDays: window });
    const financial = financialFrom({ inputs, bench, attribution: attr, readinessIndex: readiness.readinessIndex, riskMultiplier: outlook.riskMultiplier });
    const compare = compareFrom({ inputs, bench, readiness });
    return { isLive, bench, inputs, attribution: attr, readiness, cohort, outlook, financial, compare };
};
